import { BigNumber } from "bignumber.js";
import { SummaryLine } from "./summary-line";
import { formatPrice } from "./lib/format-price";
import { FeeDetails, FeeTier, FixedFeeDetails } from "./types";

const FeeDisplay = ({
	label,
	text,
	isDifferent,
	defaultText,
}: {
	label: string;
	text: string;
	isDifferent?: boolean | undefined;
	defaultText?: string;
}) => {
	if (isDifferent) {
		return (
			<span className="text-gray-600">
				{`${label} (`}
				<span className="line-through">{defaultText}</span>
				{` `}
				<span className="text-green-600">{text}</span>
				{`)`}
			</span>
		);
	}
	return <span className="text-gray-600">{`${label} (${text})`}</span>;
};

export const FeeBreakdown = ({
	feeView,
	gstPercentage,
	totalFees,
	gstFee,
	currency,
	feeDetails,
	fixedFeeDetails,
}: {
	feeView: string;
	gstPercentage: number;
	totalFees: BigNumber;
	gstFee: BigNumber;
	currency: string;
	feeDetails?: FeeDetails[];
	fixedFeeDetails?: FixedFeeDetails[];
}) => {
	return (
		<>
			{feeView !== "simplified" ? (
				<>
					{feeDetails &&
						feeDetails.map((fee) => {
							const renderField = (feeTier: FeeTier) => {
								const ratePercentage = BigNumber(
									feeTier?.rate || 0,
								).multipliedBy(100);

								if (feeTier?.no_of_years) {
									const years = formatPrice(feeTier.no_of_years, 0);
									return `${ratePercentage}% per annum for ${years} years`;
								}

								return `${ratePercentage}%`;
							};

							return (
								<SummaryLine
									field={
										<FeeDisplay
											label={fee.label}
											text={renderField(fee.applicableTier!)}
											// Commented out due to Product team's request (dont show discount, just plain text)
											// isDifferent={fee.applicableTier !== fee.tiers[0]}
											// defaultText={renderField(fee.tiers[0])}
										/>
									}
									value={`${formatPrice(
										Number(fee.calculatedFee || "0"),
									)} ${currency.toUpperCase()}`}
								/>
							);
						})}

					{fixedFeeDetails &&
						fixedFeeDetails.map((fee) => {
							const renderField = (fixedFee: FixedFeeDetails) => {
								const ratePercentage = BigNumber(
									fixedFee?.rate || 0,
								).multipliedBy(100);

								if (fixedFee?.no_of_years) {
									const years = formatPrice(fixedFee.no_of_years, 0);
									return `${ratePercentage}% per annum for ${years} years`;
								}

								return `${ratePercentage}%`;
							};

							return (
								<SummaryLine
									field={
										<FeeDisplay label={fee.label} text={renderField(fee)} />
									}
									value={`${formatPrice(
										Number(fee.calculatedFee || "0"),
									)} ${currency.toUpperCase()}`}
								/>
							);
						})}
				</>
			) : (
				totalFees.isGreaterThanOrEqualTo(0) && (
					<SummaryLine
						field={`Total fees`}
						value={`${formatPrice(totalFees.toNumber())} ${currency.toUpperCase()}`}
					/>
				)
			)}
			{gstPercentage > 0 && gstFee.isGreaterThan(0) && (
				<SummaryLine
					field={`Total local supply goods & services tax (${BigNumber(gstPercentage).multipliedBy(100)}%)`}
					value={`${formatPrice(gstFee.toNumber())} ${currency.toUpperCase()}`}
				/>
			)}
		</>
	);
};
